
import React, { createContext, useContext } from 'react';
import { PasswordContextType } from './types';
import { usePasswordState } from './hooks/usePasswordState';
import { usePasswordActions } from './hooks/usePasswordActions';
import { clearAuthSession } from './utils';

// Create context
const PasswordContext = createContext<PasswordContextType | undefined>(undefined);

// Create hook to use the context
export const usePassword = () => {
  const context = useContext(PasswordContext);
  if (!context) {
    throw new Error('usePassword must be used within a PasswordProvider');
  }
  return context;
};

export const PasswordProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Get state from custom hook
  const state = usePasswordState();
  
  // Get actions from custom hook
  const actions = usePasswordActions(state);
  
  // Stub implementations for the remaining required methods
  const verifyPasskey = (): boolean => false;
  const changePassword = (): boolean => false;
  const changePasskey = (): boolean => false;
  const setupInitialAuth = (): boolean => false;
  const resetFailedAttempts = (): void => {};
  const lockApplication = (): void => {};
  const unlockApplication = (): boolean => false;
  const hasPasswordSet = (): boolean => state.hasPassword;
  const isProtected = true;
  const accessGranted = false;
  const attemptsRemaining = state.remainingAttempts;

  const value: PasswordContextType = {
    isProtected,
    isLocked: state.isLocked,
    accessGranted,
    attemptsRemaining,
    hasPassword: state.hasPassword,
    lockoutEndTime: state.lockoutEndTime,
    remainingAttempts: state.remainingAttempts,
    verifyPassword: actions.verifyPassword,
    verifyPasskey,
    changePassword,
    changePasskey,
    resetPassword: actions.resetPassword,
    setupInitialAuth,
    resetFailedAttempts,
    lockApplication,
    unlockApplication,
    hasPasswordSet,
    createPassword: actions.createPassword,
    clearPassword: actions.clearPassword,
    passwordStrength: actions.passwordStrength,
    verifySecurePassKey: actions.verifySecurePassKey,
    hasSecurePassKey: actions.hasSecurePassKey,
    clearAuthSession,
    lockAccount: actions.lockAccount
  };

  return <PasswordContext.Provider value={value}>{children}</PasswordContext.Provider>;
};
